import React, { useEffect, useState } from 'react';
import { Check, Copy, Globe } from 'lucide-react';
import { displayUrl } from '../../lib/share';
import { toaster } from '../ui';

/**
 * A public note's link, always visible and one tap to copy — the web twin of mobile's
 * `PublicLinkRow` (mobile/src/features/notes/public-link-row.tsx).
 */
export const PublicLinkRow: React.FC<{ url: string }> = ({ url }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toaster.success({ title: 'Link copied.' });
    } catch {
      toaster.error({ title: "Couldn't copy — select the link instead." });
    }
  };

  return (
    <div className="mt-4 flex items-center gap-2.5 rounded-xl bg-surface-container-low py-2 pl-2.5 pr-2">
      <span
        aria-hidden="true"
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hl-mint text-hl-mint-ink"
      >
        <Globe size={13} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="m-0 font-body text-[0.6875rem] font-semibold text-on-surface-variant">
          Anyone with the link can view
        </p>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block truncate font-body text-[0.8125rem] text-on-surface no-underline hover:underline"
        >
          {displayUrl(url)}
        </a>
      </div>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label="Copy public link"
        className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full border-none bg-surface-container-lowest px-3 py-1.5 font-body text-[0.75rem] font-semibold text-on-surface transition-transform duration-150 hover:bg-surface-container active:scale-[0.92]"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
        {copied ? 'Copied' : 'Copy link'}
      </button>
    </div>
  );
};
